import { useMemo, useState } from "react";
import {
  CheckCircle2,
  ClipboardCheck,
  MessageSquareWarning,
  Plus,
  X,
  XCircle,
} from "lucide-react";
import { EmptyState, Field, StatusPill } from "../components/Ui";
import type {
  ApprovalRecord,
  CriticalControl,
  DesignRisk,
  DocumentRevision,
  HirarcAssessment,
} from "../types/domain";

interface ApprovalsProps {
  approvals: ApprovalRecord[];
  risks: DesignRisk[];
  hirarc: HirarcAssessment[];
  controls: CriticalControl[];
  revisions: DocumentRevision[];
  onSave: (approval: ApprovalRecord) => void;
}

export function ApprovalsView({
  approvals,
  risks,
  hirarc,
  controls,
  revisions,
  onSave,
}: ApprovalsProps) {
  const [draft, setDraft] = useState<ApprovalRecord | null>(null);
  const entities = useMemo(
    () => [
      ...risks.map((item) => ({
        type: "risk" as const,
        id: item.id,
        title: item.designElement || item.hazard || "Untitled design risk",
      })),
      ...hirarc.map((item) => ({
        type: "hirarc" as const,
        id: item.id,
        title: item.activity || item.hazard || "Untitled HIRARC",
      })),
      ...controls.map((item) => ({
        type: "control" as const,
        id: item.id,
        title: item.title || "Untitled critical control",
      })),
      ...revisions.map((item) => ({
        type: "document" as const,
        id: item.id,
        title: `${item.documentNumber || "No number"} · ${item.revision}`,
      })),
    ],
    [controls, hirarc, revisions, risks],
  );
  const titleFor = (approval: ApprovalRecord) =>
    entities.find(
      (entity) =>
        entity.type === approval.entityType && entity.id === approval.entityId,
    )?.title ?? "Deleted or unavailable record";
  const pending = approvals.filter((item) => item.status === "requested");
  const decided = approvals.filter((item) => item.status !== "requested");

  const requestApproval = () => {
    const first = entities[0];
    setDraft({
      id: crypto.randomUUID(),
      entityType: first?.type ?? "risk",
      entityId: first?.id ?? "",
      step: "Technical review",
      status: "requested",
      requester: "",
      reviewer: "",
      requestedAt: new Date().toISOString(),
      decidedAt: "",
      comment: "",
    });
  };

  const decide = (
    approval: ApprovalRecord,
    status: "approved" | "rejected" | "changes-requested",
  ) =>
    onSave({
      ...approval,
      status,
      decidedAt: new Date().toISOString(),
    });

  return (
    <>
      <section className="module-intro">
        <div>
          <span className="feature-kicker"><ClipboardCheck size={16} /> Accountable decisions</span>
          <h2>Separate authorship, review and acceptance.</h2>
          <p>
            Route safety-critical records to a named reviewer, preserve the decision comment and
            keep changes requested distinct from approval.
          </p>
        </div>
        <button className="button button-primary" disabled={!entities.length} onClick={requestApproval}>
          <Plus size={18} /> Request approval
        </button>
      </section>

      {approvals.length === 0 ? (
        <EmptyState
          icon={<ClipboardCheck size={32} />}
          title="No approval workflow started"
          action={<button className="button button-primary" disabled={!entities.length} onClick={requestApproval}><Plus size={18} /> Request first approval</button>}
        >
          Add a design risk, HIRARC row, critical control or document revision, then route it for
          technical review or formal acceptance.
        </EmptyState>
      ) : (
        <>
          <section className="approval-section">
            <div className="section-heading-row"><div><span className="eyebrow">Decision queue</span><h2>Awaiting review</h2></div><StatusPill status={pending.length ? "warning" : "success"}>{pending.length} pending</StatusPill></div>
            <div className="approval-list">
              {pending.map((approval) => (
                <article key={approval.id}>
                  <div><span>{approval.entityType} · {approval.step}</span><h3>{titleFor(approval)}</h3><p>Requested by {approval.requester} · reviewer {approval.reviewer}</p></div>
                  <div className="approval-buttons">
                    <button className="approve-button" onClick={() => decide(approval, "approved")}><CheckCircle2 size={16} /> Approve</button>
                    <button className="change-button" onClick={() => decide(approval, "changes-requested")}><MessageSquareWarning size={16} /> Changes</button>
                    <button className="reject-button" onClick={() => decide(approval, "rejected")}><XCircle size={16} /> Reject</button>
                  </div>
                </article>
              ))}
              {!pending.length && <p className="table-empty">No records are waiting for review.</p>}
            </div>
          </section>

          <section className="approval-section">
            <div className="section-heading-row"><div><span className="eyebrow">Decision history</span><h2>Completed decisions</h2></div></div>
            <div className="approval-list approval-history">
              {decided.map((approval) => (
                <article key={approval.id}>
                  <div><span>{approval.entityType} · {approval.step}</span><h3>{titleFor(approval)}</h3><p>{approval.comment || "No decision comment"} · {approval.decidedAt ? new Date(approval.decidedAt).toLocaleString("en-MY") : ""}</p></div>
                  <StatusPill status={approval.status === "approved" ? "success" : approval.status === "rejected" ? "danger" : "warning"}>{approval.status}</StatusPill>
                </article>
              ))}
            </div>
          </section>
        </>
      )}

      {draft && (
        <div className="modal-backdrop">
          <section className="modal-card modal-compact" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div><span className="eyebrow">New approval request</span><h2>Route an accountable decision</h2></div>
              <button className="icon-button" onClick={() => setDraft(null)} aria-label="Close"><X size={21} /></button>
            </div>
            <div className="modal-scroll">
              <div className="form-grid">
                <Field label="Record" wide>
                  <select
                    value={`${draft.entityType}:${draft.entityId}`}
                    onChange={(e) => {
                      const [entityType, entityId] = e.target.value.split(":");
                      setDraft({ ...draft, entityType: entityType as ApprovalRecord["entityType"], entityId });
                    }}
                  >
                    {entities.map((entity) => <option key={`${entity.type}:${entity.id}`} value={`${entity.type}:${entity.id}`}>{entity.type} · {entity.title}</option>)}
                  </select>
                </Field>
                <Field label="Approval step">
                  <input value={draft.step} onChange={(e) => setDraft({ ...draft, step: e.target.value })} />
                </Field>
                <Field label="Requester">
                  <input value={draft.requester} onChange={(e) => setDraft({ ...draft, requester: e.target.value })} />
                </Field>
                <Field label="Named reviewer">
                  <input value={draft.reviewer} onChange={(e) => setDraft({ ...draft, reviewer: e.target.value })} />
                </Field>
                <Field label="Review brief / comment" wide>
                  <textarea rows={4} value={draft.comment} onChange={(e) => setDraft({ ...draft, comment: e.target.value })} />
                </Field>
              </div>
            </div>
            <div className="modal-actions">
              <button className="button button-secondary" onClick={() => setDraft(null)}>Cancel</button>
              <button
                className="button button-primary"
                disabled={!draft.entityId || !draft.requester.trim() || !draft.reviewer.trim()}
                onClick={() => {
                  onSave(draft);
                  setDraft(null);
                }}
              >
                Send for review
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
